<script lang="ts">
    
    import type { Map as Mapgl } from 'mapbox-gl';
    import { Marker } from 'mapbox-gl';
    import { createEventDispatcher, getContext, onDestroy } from "svelte";
    import type { Readable } from "svelte/store";
    import type { Coordinates, Fn } from '../global';

    const map = getContext<Readable<Mapgl>>('map');
    const dispatch = createEventDispatcher();

    let marker: Marker;
    let prevClasses: string[] | null = null;
    let markerElement: HTMLElement;
    
    export let coordinates: Coordinates;
    export let width: number | null = null;
    export let height: number | null = null;
    export let zIndex: number | null = null;
    export let anchor: string = 'bottom';
    export let draggable: boolean = false;
    export let className: string | null = null;

    $: updateMarker(coordinates, width, height, draggable, zIndex, className);
    $: {
        destroyMarker();
        createMarker($map, markerElement, anchor);
    }

    onDestroy(destroyMarker);

    function createMarker(map: Mapgl, markerElement: HTMLElement, anchor: string) {
        if (marker || !map || !markerElement) {
            return;
        }

        marker = new Marker({
            element: markerElement,
            anchor: anchor as mapboxgl.Anchor,
        });

        marker.on('dragstart', emitDragstart);
        marker.on('dragend', emitDragend);

        markerElement.addEventListener('touchstart', emitTouchstart);
        markerElement.addEventListener('touchend', emitTouchend);

        updateMarker(coordinates, width, height, draggable, zIndex, className);

        marker.getElement().addEventListener('click', emitClick);
        marker.addTo(map);
    }

    function destroyMarker() {
        if (!marker) {
            return;
        }
        marker.remove();
        marker = null;
    }

    function updateMarker(
        coordinates: Coordinates,
        width: number | null,
        height: number | null,
        draggable: boolean,
        zIndex: number | null,
        className: string | null,
    ) {
        
        if (!marker) {
            return;
        }

        const element = marker.getElement();

        marker.setDraggable(draggable);
        marker.setLngLat(coordinates);

        if (width || height) {
            if (width) element.style.width = `${width}px`;
            if (height) element.style.height = `${height}px`;
        }

        if (prevClasses) {
            element.classList.remove(...prevClasses);
        }

        if (className) {
            const newClasses = className.trim().split(/\s+/);
            element.classList.add(...newClasses);
            prevClasses = newClasses;
        } else {
            prevClasses = null;
        }

        element.style.zIndex = zIndex ? `${zIndex}` : '';
    }

    function emitDragstart() {
        dispatch('dragstart', marker.getLngLat());
    }

    function emitDragend() {
        dispatch('dragend', marker.getLngLat());
    }

    function emitTouchstart() {
        dispatch('touchstart');
    }

    function emitTouchend() {
        dispatch('touchend');
    }

    function emitClick() {
        dispatch('click', marker.getLngLat());
    }

</script>

<div bind:this={markerElement}>
    <slot></slot>
</div>
